/**
 * Cadeaustaffel in plaats van korting.
 *
 * Onze leveranciers (emerginC voorop) staan geen korting op de adviesprijs toe.
 * Grotere bestellingen belonen we daarom met iets dat de klant wél waarde geeft
 * en ons geen marge kost: reisformaten, een persoonlijk huidplan en tijd van Sarah.
 *
 * Backend: elke trede is een cadeauregel op winkelmandniveau (`ruleId`),
 * nooit een coupon met korting. De staffel zelf komt straks uit de instellingen.
 */
import { founder } from './content'
import { freeShippingLine, policy, policyCopy } from './policies'
import { euro } from '../core/format/currency'
import type { GiftTier } from '../core/types/site'

export type { GiftTier }

/** De staffel zoals hij lokaal geldt. De eerste trede is altijd gratis verzending. */
export const defaultGiftTiers = (): GiftTier[] => [
  {
    threshold: policy.freeShippingFrom,
    title: 'Gratis verzending',
    phrase: 'gratis verzending',
    description: `Binnen Nederland en België. ${policyCopy.delivery}.`,
    ruleId: `free-shipping-${policy.freeShippingFrom}`,
  },
  {
    threshold: 175,
    title: 'Reisformaat naar keuze',
    phrase: 'een reisformaat cadeau',
    description: 'Kies zelf een reisformaat cleanser of toner. Ideaal voor je weekendtas.',
    ruleId: 'gift-travelsize-175',
    choices: ['Reisformaat cleanser', 'Reisformaat toner'],
  },
  {
    threshold: 250,
    title: `Huidplan + videoconsult met ${founder.firstName}`,
    phrase: `je huidplan + videoconsult met ${founder.firstName}`,
    description: `Een persoonlijk huidplan in Mijn omgeving, ${policy.giftConsultMinutes} minuten videoconsult en een reisformaat Vitamin C serum.`,
    ruleId: 'gift-consult-250',
  },
]

/** De actuele staffel, altijd oplopend gesorteerd. Wordt in place bijgewerkt (zie data/site.ts). */
export const giftTiers: GiftTier[] = defaultGiftTiers()

const ascending = (tiers: readonly GiftTier[]) => [...tiers].sort((a, b) => a.threshold - b.threshold)

/**
 * De hoogste bereikte trede in een gegeven staffel. Het beheer geeft zijn concept mee
 * (`useAdmin().site.giftTiers`), zodat een wijziging in Instellingen meteen doorwerkt.
 */
export const tierIn = (tiers: readonly GiftTier[], subtotal: number) =>
  ascending(tiers).reverse().find((t) => subtotal >= t.threshold) ?? null

/** De eerstvolgende trede in een gegeven staffel (zie `tierIn`). */
export const nextTierIn = (tiers: readonly GiftTier[], subtotal: number) =>
  ascending(tiers).find((t) => subtotal < t.threshold) ?? null

/**
 * Alles wat vrijgespeeld is, behalve gratis verzending (dat staat al bij de verzendkosten).
 * De staffel telt op: wie € 250 haalt, krijgt ook het reisformaat van € 175.
 * Puur: staffel en verzendgrens komen mee (`useSite()`), zodat een live wijziging meteen telt.
 */
export const unlockedIn = (tiers: readonly GiftTier[], freeShippingFrom: number, subtotal: number) =>
  ascending(tiers).filter((t) => subtotal >= t.threshold && t.threshold !== freeShippingFrom)

/** De cadeaus naast gratis verzending (voor vertrouwensregels en lijsten). */
export const giftsBesidesShipping = (tiers: readonly GiftTier[], freeShippingFrom: number) =>
  ascending(tiers).filter((t) => t.threshold !== freeShippingFrom)

/** Korte zinnen per trede; de verzendtrede gebruikt dezelfde zin als overal (`freeShippingLine`). */
export const tierLinesIn = (tiers: readonly GiftTier[], freeShippingFrom: number) =>
  ascending(tiers).map((t) => (t.threshold === freeShippingFrom ? freeShippingLine(freeShippingFrom) : `${t.title} vanaf ${euro(t.threshold)}`))

// ——— Oude aanroepers (lezen de gedeelde staffel). Nieuwe code: de functies hierboven met `useSite()`. ———

export const giftTierFor = (subtotal: number) => tierIn(giftTiers, subtotal)

export const unlockedGiftTiers = (subtotal: number) => unlockedIn(giftTiers, policy.freeShippingFrom, subtotal)

/** De gekozen optie, of de eerste als ze nog niets koos. `null` = deze trede heeft geen keuze. */
export const giftChoiceFor = (tier: GiftTier, chosen: Record<string, string>) =>
  tier.choices?.length ? (tier.choices.includes(chosen[tier.ruleId] ?? '') ? chosen[tier.ruleId] : tier.choices[0]) : null

export const nextGiftTier = (subtotal: number) => nextTierIn(giftTiers, subtotal)

/** Korte, afgeleide zinnen voor aankondigingsbalk en vertrouwensregels. */
export const giftTierLines = () => tierLinesIn(giftTiers, policy.freeShippingFrom)

/** Consultopties. Getters, zodat naam, duur en prijs altijd de actuele instellingen volgen. */
export const consult = {
  title: 'Persoonlijk huidconsult',
  online: {
    get name() {
      return `${policy.consult.name} — online huidconsult`
    },
    get duration() {
      return `${policy.consult.minutes} min videocall`
    },
    get price() {
      return policyCopy.consultPrice
    },
    get note() {
      return `${policyCopy.consultCredit}.`
    },
    get body() {
      return `Je stuurt vooraf foto’s en je huidige routine. We nemen ${policy.consult.minutes} minuten door wat je huid doet, wat je kunt overslaan en wat er echt nodig is. Daarna staat je plan in Mijn omgeving.`
    },
  },
  whatsapp: {
    name: 'Korte vraag via WhatsApp',
    get duration() {
      return `Antwoord binnen ${policy.responseHours} uur`
    },
    price: 'Gratis',
    body: 'Twijfel je tussen twee producten of reageert je huid ergens op? Stuur een bericht met een foto. Ik antwoord persoonlijk. Geen chatbot, geen verkooppraatje.',
  },
}
